import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { DirectionService } from '../../direction.service';
import { AuthService } from 'src/app/auth/auth.service';
import { DialogService } from 'src/app/dialog/dialog.service';
import { BranchesService } from '../../super-management/branch/branches.service';
import { GeneralManagementService } from '../../super-management/general-management.service';

@Component({
  selector: 'app-su-message-to-customer',
  templateUrl: './su-message-to-customer.component.html',
  styleUrls: ['./su-message-to-customer.component.css'],
  host: {
    class: 'fill-screen-modal-new-dialog'
  }
})
export class SuMessageToCustomerComponent implements OnInit, OnDestroy {
  isRTL: boolean = true;
  private directionSubscription: Subscription;
  isDarkMode: boolean = false;
  isLoading: boolean = false;
  isSaving: boolean = false;

  userId: string;
  userIsAuthenticated = false;
  private authStatusSub: Subscription;

  // Branch data passed from parent component
  branch: any = null;
  printingService: string = '';
  state: string = 'messageToCustomer';
  plotterId: string = ''; // Plotter ID for messageToPrintQueue
  /** מייל נמען — state === 'sendSmsToUser' */
  targetUserEmail = '';

  // Form for message
  messageForm: FormGroup;
  
  // Toggle for help request with points (messageToPrintQueue only)
  isHelpRequestForPoints: boolean = false;

  constructor(
    private directionService: DirectionService,
    private authService: AuthService,
    private dialogService: DialogService,
    private branchesService: BranchesService,
    private generalManagementService: GeneralManagementService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  async ngOnInit() {
    this.isLoading = true;
    this.directionSubscription = this.directionService.direction$.subscribe(direction => {
      this.isRTL = direction === 'rtl';
    });

    this.directionService.isDarkMode$.subscribe(isDarkMode => {
      this.isDarkMode = isDarkMode;
    });

    this.userId = this.authService.getUserId();
    this.userIsAuthenticated = this.authService.getIsAuth();
    this.authStatusSub = this.authService
      .getAuthStatusListener()
      .subscribe(isAuthenticated => {
        this.userIsAuthenticated = isAuthenticated;
        this.userId = this.authService.getUserId();
      });

    // Receive branch data from parent component
    if (this.data) {
      this.branch = this.data.branch;
      this.printingService = this.data.printingService || '';
      this.state = this.data.state || 'messageToCustomer';
      this.plotterId = this.data.plotterId || ''; // Plotter ID for messageToPrintQueue
      this.targetUserEmail = String(this.data.targetUserEmail || '').trim();
    }

    // Initialize form with current values
    const messageToCustomer = this.branch?.messageToCustomer || {};
    
    // Get default content based on state
    let defaultContent = messageToCustomer.content || '';
    if (this.state === 'messageToHelpers') {
      defaultContent = this.buildHelpersDefaultContent();
    } else if (this.state === 'messageToPrintQueue') {
      defaultContent = this.buildPrintQueueDefaultContent();
    } else if (this.state === 'sendSmsToUser') {
      defaultContent = this.buildSmsToUserDefaultContent();
    }

    // Initialize form based on state
    if (this.state === 'messageToHelpers' || this.state === 'messageToPrintQueue' || this.state === 'sendSmsToUser') {
      this.messageForm = new FormGroup({
        title: new FormControl('', []), // Not required for helpers/print queue
        content: new FormControl(defaultContent, [Validators.required]),
        buttonText: new FormControl('', []), // Not required for helpers/print queue
        isOn: new FormControl(messageToCustomer.isOn || false)
      });
    } else {
      // messageToCustomer - original behavior
      this.messageForm = new FormGroup({
        title: new FormControl(messageToCustomer.title || '', [Validators.required]),
        content: new FormControl(messageToCustomer.content || '', [Validators.required]),
        buttonText: new FormControl(messageToCustomer.buttonText || '', [Validators.required]),
        isOn: new FormControl(messageToCustomer.isOn || false)
      });
    }

    this.isLoading = false;
  }

  ngOnDestroy() {
    if (this.directionSubscription) {
      this.directionSubscription.unsubscribe();
    }
    if (this.authStatusSub) {
      this.authStatusSub.unsubscribe();
    }
  }

  closeSuMessageToCustomerDialog() {
    this.dialogService.onCloseSuMessageToCustomerDialog();
  }

  getBranchSerialName(): string {
    if (!this.branch) return '';
    
    const serialName = this.branch.serial_name || 
                       this.branch?.properties?.serial_name ||
                       (this.branch?.properties?.serial ? 
                         String(this.branch.properties.serial) : '') ||
                       '';
    
    return serialName;
  }

  getBranchName(): string {
    if (!this.branch || !this.printingService) return '';
    
    const branchSerialName = this.getBranchSerialName();
    
    if (!branchSerialName) {
      return '';
    }
    
    if (this.printingService === 'express') {
      return `choose-branch.naming.express.${branchSerialName}`;
    }
    
    return `choose-branch.naming.${this.printingService}.${branchSerialName}`;
  }

  getPrintingServiceIcon(): string {
    if (this.printingService === 'plotter') {
      return 'assets/images/plotter-icon.svg';
    }
    return `assets/images/${this.printingService}-icon.svg`;
  }

  getBranchIcon(): string {
    if (!this.branch) return '';
    
    const branchSerialName = this.getBranchSerialName();
    if (!branchSerialName) return '';
    return `assets/images/branches/${branchSerialName}.png`;
  }

  getDisplayText(): string {
    return this.getBranchName();
  }

  buildHelpersDefaultContent(): string {
    // Build the default content for helpers message
    const needHelpText = this.translate.instant('su-management.message-to-customer.helpers-need-help');
    const branchName = this.translate.instant(this.getBranchName());
    const branchSuffix = this.translate.instant('su-management.message-to-customer.helpers-branch-suffix');
    const contactSupportText = this.translate.instant('su-management.message-to-customer.helpers-contact-support');
    const removeFromListText = this.translate.instant('su-management.message-to-customer.helpers-remove-from-list');
    
    // Get user ID for remove URL
    const userId = this.authService.getUserId();
    
    // Build simple URLs
    const helpersUrl = `https://print.eazix.io/helpers`;
    const removeUrl = `https://print.eazix.io/remove?x=${userId}`;
    
    return `${needHelpText} ${branchName}${branchSuffix}. ${contactSupportText} ${helpersUrl} ${removeFromListText} ${removeUrl}`;
  }

  buildPrintQueueDefaultContent(): string {
    return this.translate.instant('su-management.send-message-to-print-queue.default-content');
  }

  buildSmsToUserDefaultContent(): string {
    return this.translate.instant('su-management.send-sms-to-user.content-greeting', {
      email: this.targetUserEmail || '',
    });
  }

  buildHelpRequestForPointsContent(): string {
    const part1 = this.translate.instant('su-management.send-message-to-print-queue.help-request-part1');
    const branchName = this.translate.instant(this.getBranchName());
    const part2 = this.translate.instant('su-management.send-message-to-print-queue.help-request-part2');
    const helpersUrl = `https://print.eazix.io/helpers`;
    
    return `${part1} ${branchName} ${part2} ${helpersUrl}`;
  }

  onHelpRequestToggleChange(): void {
    if (this.state === 'messageToPrintQueue') {
      if (this.isHelpRequestForPoints) {
        this.messageForm.get('content')?.setValue(this.buildHelpRequestForPointsContent());
      } else {
        this.messageForm.get('content')?.setValue(this.buildPrintQueueDefaultContent());
      }
    }
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.style.display = 'none';
    }
  }

  onSave() {
    if (this.messageForm.invalid || this.isSaving) {
      return;
    }

    this.isSaving = true;
    const formValue = this.messageForm.value;
    const branchId = this.branch?._id || this.branch?.id;

    if (this.state === 'sendSmsToUser') {
      const email = (this.targetUserEmail || '').trim();
      if (!email) {
        console.error('Target user email missing for sendSmsToUser');
        this.isSaving = false;
        return;
      }
      this.generalManagementService
        .sendSmsToUser({
          targetUserEmail: email,
          messageContent: formValue.content,
          printingService: this.printingService || undefined,
          branchId: branchId || this.data?.branchId || undefined,
        })
        .subscribe({
          next: () => {
            this.isSaving = false;
            this.closeSuMessageToCustomerDialog();
            this.snackBar.open(
              this.translate.instant('su-management.send-sms-to-user.success'),
              '',
              { duration: 3000, verticalPosition: 'top' }
            );
            if (this.data?.onUpdate) {
              this.data.onUpdate();
            }
          },
          error: (error) => {
            console.error('Error sending SMS to user:', error);
            this.isSaving = false;
          },
        });
      return;
    }

    if (!branchId) {
      console.error('Branch ID not found');
      this.isSaving = false;
      return;
    }

    // Handle different states
    if (this.state === 'messageToHelpers') {
      // Send message to helpers
      this.generalManagementService.sendMessageToHelpers(
        this.printingService,
        branchId,
        formValue.content
      ).subscribe({
        next: (response) => {
          console.log('Message sent to helpers successfully:', response);
          this.isSaving = false;
          this.closeSuMessageToCustomerDialog();
          // Show success snackbar
          this.snackBar.open(
            this.translate.instant('su-management.send-message-to-helpers.success'),
            '',
            {
              duration: 3000,
              verticalPosition: 'top'
            }
          );
        },
        error: (error) => {
          console.error('Error sending message to helpers:', error);
          console.log('FULL ERROR RESPONSE (stringified):', JSON.stringify(error, null, 2));
          this.isSaving = false;
          // Error dialog will be shown automatically by ErrorInterceptor
          // Don't close the dialog on error
        }
      });
    } else if (this.state === 'messageToPrintQueue') {
      // Send message to print queue users (plotter only)
      // Use plotterId (the printer's _id) instead of branchId
      if (!this.plotterId) {
        console.error('Plotter ID not found');
        this.isSaving = false;
        return;
      }
      this.generalManagementService.sendMessageToPrintQueue(
        this.plotterId,
        formValue.content
      ).subscribe({
        next: (response) => {
          console.log('Message sent to print queue successfully:', response);
          this.isSaving = false;
          this.closeSuMessageToCustomerDialog();
          // Show success snackbar
          this.snackBar.open(
            this.translate.instant('su-management.send-message-to-print-queue.success'),
            '',
            {
              duration: 3000,
              verticalPosition: 'top'
            }
          );
        },
        error: (error) => {
          console.error('Error sending message to print queue:', error);
          console.log('FULL ERROR RESPONSE (stringified):', JSON.stringify(error, null, 2));
          this.isSaving = false;
          // Error dialog will be shown automatically by ErrorInterceptor
          // Don't close the dialog on error
        }
      });
    } else {
      // Original behavior for messageToCustomer
      this.branchesService.updateMessageToCustomer(
        this.printingService,
        branchId,
        formValue.title,
        formValue.content,
        formValue.buttonText,
        formValue.isOn
      ).subscribe({
        next: (response) => {
          console.log('Message updated successfully:', response);
          this.isSaving = false;
          this.closeSuMessageToCustomerDialog();
          // Optionally refresh the branch data
          if (this.data.onUpdate) {
            this.data.onUpdate();
          }
        },
        error: (error) => {
          console.error('Error updating message:', error);
          this.isSaving = false;
          // Handle error - could show error message
        }
      });
    }
  }
}

